import { createFileRoute } from "@tanstack/react-router";
import { PageDatesBadge } from "@/components/site/PageDatesBadge";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { LicensingDisclosure } from "@/components/site/LicensingDisclosure";
import { SITE } from "@/data/site";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/legal/privacy";

export const Route = createFileRoute("/legal/privacy")({
  head: () =>
    buildHead({
      title: "Privacy Policy | Simply Approved Mortgages",
      description: "How Simply Approved Mortgages collects, uses, and protects your information.",
      url: URL,
      keywords: "privacy policy, data collection policy, mortgage broker privacy, personal information policy",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Legal", url: "/legal" },
        { name: "Privacy Policy", url: URL },
      ],
      localBusiness: false,
    }),
  component: () => (
    <SiteShell>
      <PageHero eyebrow="Legal" title="Privacy Policy" />
      <PageEssentials />
      <Section>
        <div className="prose prose-slate mx-auto max-w-3xl">
          <PageDatesBadge className="not-prose mb-4" />
          <p>Simply Approved Mortgages LLC ("we", "us") is a licensed mortgage broker. This policy explains what we collect through this website, why we collect it, who we share it with, and how you can reach us about it.</p>

          <h2>Information we collect</h2>
          <ul>
            <li><strong>Contact details</strong> you submit — name, email address, phone number, and mailing or entity address.</li>
            <li><strong>Scenario and property information</strong> — subject property address, purchase price or estimated value, rents and operating expenses, loan purpose and amount, target leverage, and self-reported credit range.</li>
            <li><strong>Entity information</strong> for business-purpose requests — LLC or corporation name, state of formation, and guarantor details.</li>
            <li><strong>Rate quote selections</strong> — the option you clicked in the rate center, so your loan officer can see exactly what you were shown.</li>
            <li><strong>Website and device data</strong> — pages viewed, referrer, approximate location, device and browser type, and analytics identifiers.</li>
          </ul>
          <p>We do not collect Social Security numbers, dates of birth, or income documentation through this website. Those are only requested later, through a secure channel, if you choose to proceed to a formal application.</p>

          <h2>Why we collect it</h2>
          <ul>
            <li>To respond to your inquiry and discuss loan scenarios with you.</li>
            <li>To obtain indicative pricing and, where you request it, to shop your scenario to wholesale lenders as your broker.</li>
            <li>To prepare pre-qualification summaries, rent and revenue estimates, and other reports you request.</li>
            <li>To meet recordkeeping, licensing, anti-fraud, and other legal obligations.</li>
            <li>To measure and improve our website.</li>
          </ul>

          <h2>Who we share it with</h2>
          <p>We are a broker, not a direct lender, so completing a request generally means sharing your information with wholesale lenders and investors so they can price and underwrite your scenario. We also use service providers that host our systems, send our email, run our analytics, and supply property, rent, and market data. Those providers may only use your information to perform services for us. We may also disclose information where required by law or to protect against fraud.</p>
          <p><strong>We do not sell your personal information, and we do not share it for cross-context behavioral advertising.</strong></p>

          <h2>Financial privacy (GLBA)</h2>
          <p>As a mortgage brokerage we are a financial institution under the Gramm-Leach-Bliley Act. Nonpublic personal information you give us is collected, used, and disclosed only as described in this policy and as permitted by the GLBA Privacy Rule — principally to process, place, and service the loan request you asked us to shop. We maintain a written information security program under the GLBA Safeguards Rule covering access controls, vendor oversight, and disposal of records. We do not disclose nonpublic personal information about you to nonaffiliated third parties except as permitted by law or as needed to carry out your request.</p>

          <h2>Cookies and analytics</h2>
          <p>We use cookies and similar technologies to keep the site working, remember your calculator inputs, and measure traffic and page performance. You can block or delete cookies in your browser settings; some features, including saved quote selections, may stop working if you do. We do not use cookies to sell your information or to build cross-context behavioral advertising profiles.</p>

          <h2>Calls, texts, and email (TCPA)</h2>
          <p>When you submit a form or request a quote, you consent to be contacted about your inquiry at the phone number(s) and email address(es) you provide — including by live agent, prerecorded or artificial voice, SMS/MMS text, email, and AI-assisted voice or chat follow-up. Consent is not a condition of any loan or service. Message and data rates may apply. Reply STOP to any text to opt out of texts, use the unsubscribe link in any marketing email, or ask us in writing to place you on our internal do-not-call list.</p>

          <h2>Retention</h2>
          <p>We keep inquiry and loan file records for as long as needed to serve you and to satisfy applicable recordkeeping and licensing requirements, and then delete or de-identify them.</p>

          <h2>Your choices</h2>
          <p>You may ask us to access, correct, or delete the information you gave us, and you may unsubscribe from marketing email at any time using the link in any message. Depending on where you live, you may have additional rights under state privacy law. Call {SITE.phone} or use our <a href="/contact">contact form</a> and we will respond.</p>

          <h2>Security</h2>
          <p>We use administrative, technical, and physical safeguards designed to protect your information. No method of transmission or storage is completely secure, so please do not email sensitive documents to us unsolicited.</p>

          <h2>Contact</h2>
          <p>Questions about this policy? Call {SITE.phone} or use our <a href="/contact">contact form</a>.</p>
          <LicensingDisclosure className="not-prose mt-6 text-sm text-muted-foreground" />
        </div>
      </Section>
    <PageOutro />
      </SiteShell>
  ),
});
